import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useContent, useLanguage, useUI, useLocalPath } from '../context/LanguageContext';

const HeroSection = () => {
  const content = useContent();
  const ui = useUI();
  const localPath = useLocalPath();
  const [currentVariation, setCurrentVariation] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentVariation((prev) => (prev + 1) % content.heroVariations.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [content.heroVariations.length]);

  const variation = content.heroVariations[currentVariation];

  return (
    <section
      className="relative min-h-screen flex flex-col justify-center overflow-hidden"
      style={{ background: 'radial-gradient(ellipse at 30% 50%, rgba(59,130,246,0.06) 0%, #0d1117 70%)' }}
    >
      <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: 'linear-gradient(rgba(59,130,246,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(59,130,246,0.3) 1px, transparent 1px)',
        backgroundSize: '60px 60px'
      }} />

      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 w-full pt-32 pb-16 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="flex items-center gap-2 mb-10"
        >
          <span className="w-2 h-2 rounded-full bg-[#3b82f6] animate-pulse" />
          <span className="font-mono text-[11px] tracking-[0.2em] text-[#3b82f6] uppercase">
            {ui.statusBadge}
          </span>
        </motion.div>

        <div className="relative min-h-[280px] sm:min-h-[340px] lg:min-h-[380px]">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentVariation}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.6 }}
            >
              <h1 className="text-[clamp(52px,9vw,140px)] font-black leading-[0.9] tracking-[-0.03em] text-[#e6edf3]">
                {variation.line1}
                <br />
                {variation.line2}
              </h1>
              <h1 className="text-[clamp(52px,9vw,140px)] font-black leading-[0.9] tracking-[-0.03em] text-[#3b82f6] mt-1">
                {variation.accent}
              </h1>
            </motion.div>
          </AnimatePresence>
        </div>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="font-mono text-[12px] leading-[1.8] text-[#7d8590] max-w-xl mt-6"
        >
          {content.heroSubheadline}
        </motion.p>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="flex items-center gap-2 mt-6"
        >
          {content.heroVariations.map((variation, i) => (
            <button
              key={`hero-variation-${variation.line1}-${i}`}
              onClick={() => setCurrentVariation(i)}
              className={`h-1 rounded-full transition-all duration-500 ${
                i === currentVariation ? 'w-8 bg-[#3b82f6]' : 'w-3 bg-[#21262d]'
              }`}
            />
          ))}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.5 }}
          className="mt-12 flex flex-wrap items-center gap-4"
        >
          <button
            disabled
            className="font-mono text-[11px] tracking-[0.15em] uppercase bg-[#3b82f6]/50 text-white/70 px-8 py-3.5 rounded-full cursor-not-allowed font-semibold"
          >
            {ui.seeOurPlatforms}
          </button>
          <Link
            to={localPath('/about/')}
            className="font-mono text-[11px] tracking-[0.15em] uppercase border border-[#21262d] text-[#e6edf3] px-8 py-3.5 rounded-full hover:border-[#3b82f6]/50 hover:bg-[#3b82f6]/5 transition-all duration-300 font-medium"
          >
            {ui.howWeThink}
          </Link>

          <div className="flex items-center gap-8 ml-auto">
            {content.heroStats.map((stat) => (
              <div key={stat.label} className="flex items-baseline gap-2">
                <span className="text-lg font-bold text-[#3b82f6]">{stat.value}</span>
                <span className="font-mono text-[10px] tracking-[0.1em] text-[#7d8590] uppercase hidden sm:inline">{stat.label}</span>
              </div>
            ))}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 1.0 }}
          className="mt-20 flex items-center gap-2 cursor-pointer group"
          onClick={() => {
            const el = document.querySelector('#what-we-do');
            if (el) el.scrollIntoView({ behavior: 'smooth' });
          }}
        >
          <ChevronDown size={14} className="text-[#7d8590] group-hover:text-[#3b82f6] transition-colors animate-bounce" />
          <span className="font-mono text-[10px] tracking-[0.15em] text-[#7d8590] group-hover:text-[#3b82f6] transition-colors uppercase">
            {ui.scroll}
          </span>
        </motion.div>
      </div>
    </section>
  );
};

export default HeroSection;
